import { useEffect } from "react";
import { navigate, ROUTES } from "../lib/router";
import { cn } from "../lib/classNames";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * PC/SP 共通のハンバーガーメニュー Drawer。右からスライドインし、
 * 背景に半透明オーバーレイ + Escape で閉じる。
 *
 * SP では幅 85vw (max 320px)、PC でも同じ幅で右からスライドイン。
 * メニュー項目はサイト全体ナビゲーションとして「About / Privacy /
 * Terms / Disclaimer」を提供する (AdSense 審査に必要な legal pages)。
 */
export function HamburgerMenu({ open, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <>
      {/* 背景オーバーレイ */}
      <button
        type="button"
        aria-label="メニューを閉じる"
        aria-hidden={!open}
        tabIndex={open ? 0 : -1}
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      {/* Drawer 本体 */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="メインメニュー"
        aria-hidden={!open}
        className={cn(
          "fixed right-0 top-0 z-50 flex h-[100dvh] w-[85vw] max-w-[320px] flex-col border-l border-white/10 bg-slate-950/98 shadow-2xl shadow-black/70 backdrop-blur-xl transition-transform duration-300 ease-out",
          open ? "translate-x-0" : "pointer-events-none translate-x-full",
        )}
      >
        <div className="safe-top flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 pb-2.5">
          <h2 className="text-sm font-semibold text-slate-50">メニュー</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="メニューを閉じる"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-slate-800/80 text-slate-200 transition hover:bg-slate-700"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav aria-label="サイトナビゲーション" className="thin-scroll flex-1 overflow-y-auto p-3">
          <ul className="flex flex-col gap-1">
            <MenuItem icon="home" label="ホーム (マップ)" onClick={() => go(ROUTES.home)} />
            <MenuItem icon="info" label="このサイトについて" onClick={() => go(ROUTES.about)} />
            <MenuItem icon="shield" label="プライバシーポリシー" onClick={() => go(ROUTES.privacy)} />
            <MenuItem icon="doc" label="利用規約" onClick={() => go(ROUTES.terms)} />
            <MenuItem icon="warn" label="免責事項" onClick={() => go(ROUTES.disclaimer)} />
          </ul>
        </nav>

        <div className="safe-bottom shrink-0 border-t border-white/10 px-4 py-3 text-[11px] text-slate-500">
          <p>非公式・ファンメイドのマップです。</p>
          <p className="mt-0.5">
            © {new Date().getFullYear()} 食べログ ラーメン百名店マップ
          </p>
        </div>
      </aside>
    </>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: "home" | "info" | "shield" | "doc" | "warn";
  label: string;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[14px] font-medium text-slate-200 transition hover:bg-white/5 active:bg-white/10"
      >
        <MenuIcon kind={icon} />
        <span>{label}</span>
      </button>
    </li>
  );
}

function MenuIcon({ kind }: { kind: "home" | "info" | "shield" | "doc" | "warn" }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    className: "shrink-0 text-slate-400",
  };
  switch (kind) {
    case "home":
      return (
        <svg {...common}>
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      );
    case "info":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      );
    case "shield":
      return (
        <svg {...common}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        </svg>
      );
    case "doc":
      return (
        <svg {...common}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="9" y1="13" x2="15" y2="13" />
          <line x1="9" y1="17" x2="15" y2="17" />
        </svg>
      );
    case "warn":
      return (
        <svg {...common}>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
  }
}
